// src/components/assistant/MentionMenu.tsx
//
// Lista de autocompletado del compositor (@ agentes, / skills, # herramientas).
// Patrón combobox: el foco SE QUEDA en el textarea (aria-activedescendant); ↑/↓
// mueven, Enter/Tab eligen, Esc cierra (lo maneja ChatPane). En táctil basta un
// toque: onPointerDown evita que el textarea pierda el foco y cierre el teclado.

import { Bot, Wrench, Zap, X } from 'lucide-react';
import type { MentionItem, MentionKind } from './mentions';

export const KIND_LABEL: Record<MentionKind, string> = {
  agent: 'Agentes',
  skill: 'Skills',
  tool: 'Herramientas',
};

const EMPTY: Record<MentionKind, string> = {
  agent: 'No hay agentes con ese nombre.',
  skill: 'No hay skills con ese nombre.',
  tool: 'No hay herramientas con ese nombre.',
};

export function KindIcon({ kind, size = 14 }: { kind: MentionKind; size?: number }) {
  if (kind === 'agent') return <Bot size={size} aria-hidden />;
  if (kind === 'skill') return <Zap size={size} aria-hidden />;
  return <Wrench size={size} aria-hidden />;
}

interface MentionMenuProps {
  id: string;
  kind: MentionKind;
  items: MentionItem[];
  activeIndex: number;
  loading?: boolean;
  onPick: (item: MentionItem) => void;
  onHover: (index: number) => void;
}

export function optionId(menuId: string, i: number) {
  return `${menuId}-opt-${i}`;
}

export function MentionMenu({ id, kind, items, activeIndex, loading, onPick, onHover }: MentionMenuProps) {
  return (
    <div className="absolute bottom-full left-0 right-0 z-30 mb-2 overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-xl">
      <p className="flex items-center gap-1.5 border-b border-border px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        <KindIcon kind={kind} size={12} /> {KIND_LABEL[kind]}
        <span className="ml-auto hidden font-normal normal-case tracking-normal sm:inline">↑↓ para moverte · Enter para elegir · Esc para cerrar</span>
      </p>
      <ul id={id} role="listbox" aria-label={KIND_LABEL[kind]} className="max-h-[min(45vh,18rem)] overflow-y-auto p-1">
        {loading && items.length === 0 && <li className="px-3 py-2.5 text-sm text-muted-foreground">Cargando…</li>}
        {!loading && items.length === 0 && <li className="px-3 py-2.5 text-sm text-muted-foreground">{EMPTY[kind]}</li>}
        {items.map((it, i) => {
          const active = i === activeIndex;
          return (
            <li
              key={`${it.kind}-${it.id}`}
              id={optionId(id, i)}
              role="option"
              aria-selected={active}
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => onPick(it)}
              onMouseEnter={() => onHover(i)}
              className={`flex cursor-pointer items-start gap-2.5 rounded-lg px-2.5 py-2 ${active ? 'bg-muted' : 'hover:bg-muted/60'}`}
            >
              <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                {it.icon ? <span aria-hidden>{it.icon}</span> : <KindIcon kind={it.kind} />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-foreground">{it.label}</span>
                {it.description && <span className="line-clamp-2 block text-xs text-muted-foreground">{it.description}</span>}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Chip quitable del compositor (agente activo, skill o herramienta elegida). */
export function MentionChip({ item, onRemove, prefix }: { item: MentionItem; onRemove?: () => void; prefix?: string }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1 rounded-full border border-primary/30 bg-primary/10 py-0.5 pl-2 pr-1 text-xs font-medium text-primary">
      {item.icon ? <span aria-hidden>{item.icon}</span> : <KindIcon kind={item.kind} size={12} />}
      <span className="truncate">
        {prefix}
        {item.label}
      </span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Quitar ${item.label}`}
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full hover:bg-primary/20"
        >
          <X size={12} aria-hidden />
        </button>
      )}
    </span>
  );
}
